import {
    IVanillaReaderBookmarkData,
    IVanillaReaderHighlightData,
    IVanillaReaderPublicationData,
    IVanillaReaderPublicationStateData,
    VanillaProgressEventCallback,
    VanillaPublicationShelfItemCallback,
    VanillaPublicationShelfUpdatedCallback,
    VanillaVoidCallback,
} from '../VanillaReader/VanillaReaderModel';
import {VanillaReaderAppStorage} from '../VanillaReaderApp/VanillaReaderAppStorage';
import {
    IVanillaReaderAppEvent_PUBLICATION_DOWNLOAD_PROGRESS_UPDATED,
} from '../VanillaReaderEventBus/IVanillaReaderAppEventTypeMap';
import {VanillaReaderEventBus} from '../VanillaReaderEventBus/VanillaReaderEventBus';
import {VanillaReaderShelfItem} from './VanillaReaderShelfItem';

/**
 *
 * # VanillaReaderBookshelf
 *
 * ## RESPONSIBILITIES
 *
 * The bookshelf holds and manages `VanillaReaderShelfItems`. Shelf items in turn manages the state of publications, such
 * as metadata, reading position, bookmarks and highlights etc. Think of the bookshelf as the place where the app keeps
 * its stuff during runtime, and between sessions.
 *
 * Since a large part of the responsibility of the bookshelf is to manage state, it uses all the store instances in
 * the `VanillaReaderAppStorage` class that store publication related data.
 *
 * ## RELATED TYPES
 *
 * - VanillaReaderShelfItem
 * - VanillaReaderAppStorage
 *
 * */
export class VanillaReaderBookshelf {

    private _onShelfItemAddedCallback: VanillaPublicationShelfItemCallback | undefined;
    private _onShelfItemUpdatedCallback: VanillaPublicationShelfItemCallback | undefined;
    private _onShelfItemDeletedCallback: VanillaVoidCallback | undefined;
    private _onShelfItemDownloadProgressUpdatedCallback: VanillaProgressEventCallback | undefined;
    private _onShelfItemDownloadCompletedCallback: VanillaProgressEventCallback | undefined;
    private _onBookshelfUpdatedCallback: VanillaPublicationShelfUpdatedCallback | undefined;

    constructor(private _vanillaReaderAppStorage: VanillaReaderAppStorage) {

        this._vanillaReaderAppStorage.publicationDataStore.onPublicationDataAdded(this._onPublicationDataAdded);
        this._vanillaReaderAppStorage.publicationDataStore.onPublicationDataDeleted(this._onPublicationDataDeleted);
        this._vanillaReaderAppStorage.publicationDataStore.onPublicationDataUpdated(this._onPublicationDataUpdated);

        // Highlights callbacks

        this._vanillaReaderAppStorage.highlightDataStore.onHighlightAdded(this._onHighlightAdded);
        this._vanillaReaderAppStorage.highlightDataStore.onHighlightUpdated(this._onHighlightUpdated);
        this._vanillaReaderAppStorage.highlightDataStore.onHighlightDeleted(this._onHighlightDeleted);

        // Bookmarks callbacks

        this._vanillaReaderAppStorage.bookmarkDataStore.onBookmarkAdded(this._onBookmarkAdded);
        this._vanillaReaderAppStorage.bookmarkDataStore.onBookmarkDeleted(this._onBookmarkDeleted);

        // StoredResource callback

        this._vanillaReaderAppStorage.streamedResourcesDataStore.onResourceDeleted(this._onResourceDeleted);

        VanillaReaderEventBus.addEventListener('PUBLICATION_DOWNLOAD_PROGRESS_UPDATED', this._onPublicationStreamedDataDownloadProgressUpdated);

    }

    onShelfItemDeleted(callback: VanillaVoidCallback): void {
        this._onShelfItemDeletedCallback = callback;
    }

    onShelfItemAdded(callback: VanillaPublicationShelfItemCallback): void {
        this._onShelfItemAddedCallback = callback;
    }

    onShelfItemUpdated(callback: VanillaPublicationShelfItemCallback): void {
        this._onShelfItemUpdatedCallback = callback;
    }

    onShelfItemDownloadProgressUpdated(callback: VanillaProgressEventCallback): void {
        this._onShelfItemDownloadProgressUpdatedCallback = callback;
    }

    onShelfItemDownloadProgressCompleted(callback: VanillaProgressEventCallback): void {
        this._onShelfItemDownloadCompletedCallback = callback;
    }

    onBookshelfUpdated(callback: VanillaPublicationShelfUpdatedCallback): void {
        this._onBookshelfUpdatedCallback = callback;
    }

    async hasShelfItemById(id: string): Promise<boolean> {
        return this._vanillaReaderAppStorage.publicationDataStore.hasPublicationData(id);
    }

    async hasShelfItemByFileName(fileName: string): Promise<boolean> {
        return this._vanillaReaderAppStorage.publicationDataStore.hasPublicationDataByFileName(fileName);
    }

    async fetchShelfItems(storedOnly: boolean = true): Promise<VanillaReaderShelfItem[]> {
        let shelfItems: VanillaReaderShelfItem[] = [];

        // Get all stored publications.
        let vanillaReaderPublicationData = await this._vanillaReaderAppStorage.publicationDataStore.fetchAllPublicationData();

        for (const item of vanillaReaderPublicationData) {
            if (item.id && item.fileSourceUri) {

                // All stored publications has uri that reflects the protocol that was used to originally fetch the file.
                // It will be "file://" if it was loaded from disc, and "http://" if it loaded from the web.
                if (item.fileSourceUri.includes('://')) {

                    if (storedOnly && !await this.shelfItemHasStoredResource(item.fileSourceUri)) {
                        continue;
                    }

                    try {
                        let shelfItem = await VanillaReaderShelfItem.instantiate(
                            item,
                            this._vanillaReaderAppStorage,
                        );
                        shelfItems.push(shelfItem);
                    } catch (e: unknown) {
                        console.warn(`VanillaReaderBookshelf.fetchShelfItems(), Failed to load shelf item "${item.id}"`);
                    }
                }
            }
        }
        //console.log('fetchShelfItems end', Date.now() -startTime);
        return shelfItems;
    }

    async fetchShelfItemById(id: string): Promise<VanillaReaderShelfItem | undefined> {
        let vanillaReaderPublicationData = await this._vanillaReaderAppStorage.publicationDataStore.fetchPublicationData(id);
        if (!vanillaReaderPublicationData) {
            return undefined;
        }

        return VanillaReaderShelfItem.instantiate(
            vanillaReaderPublicationData,
            this._vanillaReaderAppStorage,
        );

    }

    async fetchShelfItemByFileName(fileName: string): Promise<VanillaReaderShelfItem | undefined> {
        let vanillaReaderPublicationData = await this._vanillaReaderAppStorage.publicationDataStore.fetchPublicationDataByFileName(fileName);

        if (!vanillaReaderPublicationData?.id || !vanillaReaderPublicationData?.fileSourceUri) {
            return undefined;
        }

        if (!vanillaReaderPublicationData.fileSourceUri.includes('://')) {
            return undefined;
        }

        return VanillaReaderShelfItem.instantiate(
            vanillaReaderPublicationData,
            this._vanillaReaderAppStorage,
        );

    }

    async deleteShelfItem(shelfItemData: IVanillaReaderPublicationData): Promise<void> {
        if (shelfItemData.id) {
            await this._vanillaReaderAppStorage.publicationDataStore.deletePublicationDataByFileName(shelfItemData.fileName);
            await this._vanillaReaderAppStorage.publicationDataStore.deletePublicationOptionsData(shelfItemData.id);
            await this._vanillaReaderAppStorage.publicationDataStore.deletePublicationCacheData(shelfItemData.id);
            await this._vanillaReaderAppStorage.publicationDataStore.clearPublicationReadingPositionData(shelfItemData.id);
            await this._vanillaReaderAppStorage.streamedResourcesDataStore.deleteResourceByName(shelfItemData.fileName);

            let updatedShelfItems = await this.fetchShelfItems();

            if (this._onShelfItemDeletedCallback) {
                this._onShelfItemDeletedCallback();
            }

            if (this._onBookshelfUpdatedCallback) {
                this._onBookshelfUpdatedCallback(updatedShelfItems);
            }

        } else {
            console.warn(`Unable to delete publication ${shelfItemData.fileName} since it did not have an have a valid id.`);
        }
    }

    async addShelfItem(fileName: string, fileSourceUri: string, mimetype?: string): Promise<VanillaReaderShelfItem> {
        let fileExtension = fileName.slice(fileName.lastIndexOf('.') + 1).toLowerCase();
        let fileType = VanillaReaderBookshelf._getTypeFromFileExtension(fileExtension);
        if (!fileType) {
            throw new DOMException(`VanillaReaderShelfItem(), file type "${fileExtension}" is not a valid format.`);
        }

        mimetype = mimetype || fileType;

        return VanillaReaderShelfItem.create(fileName, fileSourceUri, mimetype, this._vanillaReaderAppStorage);

    }

    async shelfItemHasStoredResource(publicationUrl: string): Promise<boolean> {
        return this._vanillaReaderAppStorage.streamedResourcesDataStore.hasResource(publicationUrl);
    }

    /**
     *
     * PRIVATE METHODS
     *
     * */

    private static _getTypeFromFileExtension(extension: string): string | undefined {

        let fileExtToTypeMap: Map<string, string> = new Map([
            ['epub', 'application/epub+zip'],
            ['lpf', 'application/lpf+zip'],
            ['pdf', 'application/pdf'],
        ]);

        return fileExtToTypeMap.get(extension);
    }

    private _onPublicationDataAdded = async (publicationData: IVanillaReaderPublicationStateData) => {
        if (this._onShelfItemAddedCallback) {
            let shelfItem = await this.fetchShelfItemById(publicationData.id);
            if (!shelfItem) {
                console.warn(`VanillaReaderBookshelf._onPublicationDataAdded(), Publication "${publicationData.id}" not found in the store.`);
                return;
            }
            this._onShelfItemAddedCallback(shelfItem);
        }
    };

    private _onPublicationDataUpdated = async (publicationData: IVanillaReaderPublicationStateData) => {
        if (this._onShelfItemUpdatedCallback) {
            let shelfItem = await this.fetchShelfItemById(publicationData.id);

            if (!shelfItem) {
                console.warn(`VanillaReaderBookshelf._onPublicationDataUpdated(), Publication "${publicationData.id}" not found in the store.`);
                return;
            }

            this._onShelfItemUpdatedCallback(shelfItem);
        }
    };

    private _onPublicationDataDeleted = () => {
        if (this._onShelfItemDeletedCallback) {
            this._onShelfItemDeletedCallback();
        }
    };

    private _onPublicationStreamedDataDownloadProgressUpdated = (ev: CustomEvent<IVanillaReaderAppEvent_PUBLICATION_DOWNLOAD_PROGRESS_UPDATED>) => {
        if (ev.detail.publicationId) {
            if (ev.detail.progress < 1) {
                if (this._onShelfItemDownloadProgressUpdatedCallback) {
                    this._onShelfItemDownloadProgressUpdatedCallback(ev.detail.progress, ev.detail.publicationId);
                }
            } else {
                if (this._onShelfItemDownloadCompletedCallback) {
                    this._onShelfItemDownloadCompletedCallback(ev.detail.progress, ev.detail.publicationId);
                }
            }
        }
    };

    private _onHighlightAdded = (highlight: IVanillaReaderHighlightData, silent?: boolean) => {
        // The `silent` parameter tells us that we should not send an event to the UI. This is used when the `addHighlight`
        // method is called repeatedly from the `addHighlights` batch method.
        if (!silent) {
            VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('HIGHLIGHT_ADDED', {highlight}));
        }

        if (highlight.publicationId) {
            this._vanillaReaderAppStorage.highlightDataStore.fetchHighlightsByPublication(highlight.publicationId).then((highlights) => {
                    VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('HIGHLIGHTS_COLLECTION_UPDATED', {highlights}));
                },
            ).catch(console.warn);
        }

    };

    private _onHighlightUpdated = (highlight: IVanillaReaderHighlightData) => {
        VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('HIGHLIGHT_UPDATED', {highlight}));
        if (highlight.publicationId) {
            this._vanillaReaderAppStorage.highlightDataStore.fetchHighlightsByPublication(highlight.publicationId).then((highlights) => {
                    VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('HIGHLIGHTS_COLLECTION_UPDATED', {highlights}));
                },
            ).catch(console.warn);
        }
    };

    private _onHighlightDeleted = (highlight: IVanillaReaderHighlightData) => {
        VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('HIGHLIGHT_DELETED', {locator: highlight.locator}));
        if (highlight.publicationId) {
            this._vanillaReaderAppStorage.highlightDataStore.fetchHighlightsByPublication(highlight.publicationId).then((highlights) => {
                    VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('HIGHLIGHTS_COLLECTION_UPDATED', {highlights}));
                },
            ).catch(console.warn);
        }
    };

    private _onBookmarkAdded = (bookmarkData: IVanillaReaderBookmarkData, silent?: boolean) => {
        // The `silent` parameter tells us that we should not send an event to the UI. This is used when the `addBookmark`
        // method is called repeatedly from the `addBookmarks` batch method.
        if (!silent) {
            VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('BOOKMARK_ADDED', {bookmark: bookmarkData}));
        }
        if (bookmarkData.publicationId) {
            this._vanillaReaderAppStorage.bookmarkDataStore.fetchBookmarksByPublication(bookmarkData.publicationId).then((bookmarks) => {
                    VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('BOOKMARKS_COLLECTION_UPDATED', {bookmarks}));
                },
            ).catch(console.warn);
        }

    };

    private _onBookmarkDeleted = (bookmarkData: IVanillaReaderBookmarkData) => {
        VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('BOOKMARK_DELETED', {locator: bookmarkData.locator}));
        if (bookmarkData.publicationId) {
            this._vanillaReaderAppStorage.bookmarkDataStore.fetchBookmarksByPublication(bookmarkData.publicationId).then((bookmarks) => {
                    VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('BOOKMARKS_COLLECTION_UPDATED', {bookmarks}));
                },
            ).catch(console.warn);
        }
    };

    private _onResourceDeleted = (id: string) => {
        VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('PUBLICATION_STORED_RESOURCES_DELETED', {publicationId: id}));
    };

}
